/**
 * The bitmap sources Swift sent and how to draw a recipe over one: decoded by
 * the browser, geometry and tints drawn on a canvas (src/bitmapPlan.ts),
 * filters run by the raster pipeline (src/imageOps.ts), a GIF's frames
 * composed by src/gif.ts. No DOM beyond canvases and `ImageData`, so the
 * same store runs in the bitmap worker (src/bitmapWorker.ts) or, where the
 * browser has no worker canvases, on the page (src/bitmaps.ts).
 */

import { base64Decode } from './base64';
import { type BitmapPlan, planBitmap } from './bitmapPlan';
import { GifDecoder } from './gif';
import { applySteps, rasterFromRGBA, rasterToRGBA, workingScale } from './imageOps';
import type { BitmapRecipe, ImageDataOp } from './protocol';

/** What src/bitmaps.ts draws with: this store, or the worker running one. */
export interface Bitmaps {
  /** Keeps an `imagedata` op's source for the rest of the run. */
  add(op: ImageDataOp): void;
  has(key: string): boolean;
  sizeOf(key: string): { width: number; height: number } | undefined;
  /** `recipe` drawn at `scale` raster pixels per source pixel; null for an empty image. */
  render(recipe: BitmapRecipe, scale: number): Promise<RenderedBitmap | null>;
  /** The recipe at full resolution as a PNG file. */
  png(recipe: BitmapRecipe): Promise<Blob | null>;
}

interface StoredSource {
  width: number;
  height: number;
  /** An encoded file, decoded on first use. */
  blob?: Blob;
  /** Straight RGBA, for pixel buffers. */
  rgba?: Uint8ClampedArray<ArrayBuffer>;
  decoded?: Promise<CanvasImageSource>;
  /** A GIF file's bytes, for drawing its frames. */
  gifBytes?: Uint8Array;
  /** Its frames, parsed on first use (null: not a GIF the decoder reads). */
  gif?: GifDecoder | null;
}

/**
 * A drawing surface: an `OffscreenCanvas` where the browser has one, else a
 * detached `<canvas>`. `readBack`: its pixels are read with `getImageData`
 * (the raster pipeline), which keeps the canvas in memory instead of on the GPU.
 */
function surface(width: number, height: number, readBack = true): { canvas: HTMLCanvasElement | OffscreenCanvas; context: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D } {
  const canvas = typeof OffscreenCanvas === 'function' ? new OffscreenCanvas(width, height) : Object.assign(document.createElement('canvas'), { width, height });
  const context = canvas.getContext('2d', { willReadFrequently: readBack }) as CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null;
  if (!context) throw new Error('no 2D canvas context');
  return { canvas, context };
}

/** A recipe this store drew: pixels from the raster pipeline, or a canvas the browser drew. */
export type DrawnBitmap = ImageData | { canvas: HTMLCanvasElement | OffscreenCanvas; width: number; height: number };

/** A drawn recipe as src/bitmaps.ts gets it: from this store, or as the worker sends it. */
export type RenderedBitmap = DrawnBitmap | ImageBitmap;

/** Whether `roundRect` takes elliptical corners here (every current browser); else rounded clips take the raster pipeline. */
const canRoundRect =
  typeof OffscreenCanvasRenderingContext2D === 'function'
    ? typeof OffscreenCanvasRenderingContext2D.prototype.roundRect === 'function'
    : typeof CanvasRenderingContext2D === 'function' && typeof CanvasRenderingContext2D.prototype.roundRect === 'function';

/** The sources Swift has sent, by key, and how to draw recipes over them. */
export class BitmapStore implements Bitmaps {
  private sources = new Map<string, StoredSource>();

  add(op: ImageDataOp): void {
    if (this.sources.has(op.key)) return;
    const width = Math.max(0, op.width | 0);
    const height = Math.max(0, op.height | 0);
    if (op.data !== undefined) {
      const bytes = typeof op.data === 'string' ? base64Decode(op.data) : op.data;
      const source: StoredSource = { width, height, blob: new Blob([bytes as BlobPart], { type: op.mime ?? '' }) };
      if (op.mime === 'image/gif') source.gifBytes = bytes;
      this.sources.set(op.key, source);
      return;
    }
    const pixels = typeof op.pixels === 'string' || op.pixels === undefined ? base64Decode(op.pixels ?? '') : op.pixels;
    const rgba = new Uint8ClampedArray(width * height * 4);
    if (op.format === 'gray') {
      for (let i = 0; i < width * height; i++) {
        const v = pixels[i] ?? 0;
        rgba[i * 4] = v;
        rgba[i * 4 + 1] = v;
        rgba[i * 4 + 2] = v;
        rgba[i * 4 + 3] = 255;
      }
    } else {
      rgba.set(pixels.subarray(0, rgba.length));
    }
    this.sources.set(op.key, { width, height, rgba });
  }

  has(key: string): boolean {
    return this.sources.has(key);
  }

  sizeOf(key: string): { width: number; height: number } | undefined {
    const source = this.sources.get(key);
    return source && { width: source.width, height: source.height };
  }

  /** Frame `frame` of a GIF source, composed; null when the source is not a GIF or has no such frame. */
  private gifFrame(source: StoredSource, frame: number): CanvasImageSource | null {
    if (!source.gifBytes) return null;
    if (source.gif === undefined) source.gif = GifDecoder.parse(source.gifBytes);
    const gif = source.gif;
    const rgba = gif?.frame(frame);
    if (!gif || !rgba) return null;
    const { canvas, context } = surface(gif.width, gif.height);
    context.putImageData(new ImageData(rgba, gif.width, gif.height), 0, 0);
    return canvas;
  }

  private decode(source: StoredSource, frame?: number): Promise<CanvasImageSource> {
    if (frame !== undefined) {
      // A GIF's frame; the file as the browser decodes it when the decoder cannot read it.
      const image = this.gifFrame(source, frame);
      if (image) return Promise.resolve(image);
    }
    if (!source.decoded) {
      if (source.blob) {
        // `createImageBitmap` applies a JPEG's EXIF rotation ("from-image"),
        // which the size Swift read from the header already accounts for.
        source.decoded = createImageBitmap(source.blob);
      } else {
        const { canvas, context } = surface(Math.max(1, source.width), Math.max(1, source.height));
        context.putImageData(new ImageData(source.rgba!, Math.max(1, source.width), Math.max(1, source.height)), 0, 0);
        source.decoded = Promise.resolve(canvas);
      }
    }
    return source.decoded;
  }

  /**
   * Draws `recipe` at `scale` raster pixels per source pixel: straight RGBA
   * of the result and its size. Null for an empty image; rejects when the
   * source is unknown or the browser cannot decode it (HEIC in Chromium).
   */
  async render(recipe: BitmapRecipe, scale: number): Promise<DrawnBitmap | null> {
    if (recipe.source === null) return null;
    const source = this.sources.get(recipe.source);
    if (!source) throw new Error(`no image data for ${recipe.source}`);
    const image = await this.decode(source, recipe.frame);
    // Geometry and tints (an image as it is, or resized as Nuke does): one
    // drawing by the browser (src/bitmapPlan.ts). Filters: the raster pipeline.
    const plan = planBitmap(recipe.steps, source.width, source.height, scale);
    if (plan && (plan.clips.length === 0 || canRoundRect)) return drawPlan(plan, image, !source.rgba);
    const width = Math.max(1, Math.round(source.width * scale));
    const height = Math.max(1, Math.round(source.height * scale));
    const { context } = surface(width, height);
    // A pixel buffer (a QR code's modules) is scaled without smoothing.
    context.imageSmoothingEnabled = !source.rgba;
    context.imageSmoothingQuality = 'high';
    context.drawImage(image, 0, 0, width, height);
    const pixels = context.getImageData(0, 0, width, height).data;
    const raster = rasterFromRGBA(pixels, width, height, { x: 0, y: 0, width: source.width, height: source.height }, scale);
    const result = applySteps(raster, recipe.steps);
    if (result.width === 0 || result.height === 0) return null;
    return new ImageData(rasterToRGBA(result), result.width, result.height);
  }

  /** The recipe at full resolution (as far as `MAX_WORKING_PIXELS` allows) as a PNG file. */
  async png(recipe: BitmapRecipe): Promise<Blob | null> {
    if (recipe.source === null || !this.sources.has(recipe.source)) return null;
    const size = this.sizeOf(recipe.source)!;
    const scale = workingScale(recipe.width, recipe.height, recipe.width, recipe.height, size.width * size.height);
    const image = await this.render(recipe, scale);
    if (!image) return null;
    let canvas: HTMLCanvasElement | OffscreenCanvas;
    if (image instanceof ImageData) {
      const drawn = surface(image.width, image.height);
      drawn.context.putImageData(image, 0, 0);
      canvas = drawn.canvas;
    } else {
      canvas = image.canvas;
    }
    if ('convertToBlob' in canvas) return canvas.convertToBlob({ type: 'image/png' });
    return new Promise((resolve) => canvas.toBlob(resolve, 'image/png'));
  }
}

/** Draws a plan: clipped to its rounded rectangles, the image smoothed (unless it is a pixel buffer), then tinted. */
function drawPlan(plan: BitmapPlan, image: CanvasImageSource, smooth: boolean): DrawnBitmap | null {
  if (plan.width === 0 || plan.height === 0) return null;
  const { canvas, context } = surface(plan.width, plan.height, false);
  for (const clip of plan.clips) {
    // Each clip narrows the last, as the raster pipeline's coverages multiply.
    context.beginPath();
    context.roundRect(clip.x, clip.y, clip.width, clip.height, [{ x: clip.rx, y: clip.ry }]);
    context.clip();
  }
  context.imageSmoothingEnabled = smooth;
  context.imageSmoothingQuality = 'high';
  context.drawImage(image, plan.image.x, plan.image.y, plan.image.width, plan.image.height);
  if (plan.tint) {
    const { r, g, b, a } = plan.tint;
    context.globalCompositeOperation = 'source-in';
    context.fillStyle = `rgba(${Math.round(r * 255)}, ${Math.round(g * 255)}, ${Math.round(b * 255)}, ${a})`;
    context.fillRect(0, 0, plan.width, plan.height);
  }
  return { canvas, width: plan.width, height: plan.height };
}
